<template>
  <div class="observation-detail-container">
    <div class="detail-header">
      <div class="header-content">
        <h2 class="detail-title">Détail de l'observation</h2>
        <div class="observation-meta">
          <span class="meta-badge">
            <i class="icon">📅</i>
            {{ formatDate(observation.date) }}
          </span>
          <span class="meta-badge" v-if="observation.duration">
            <i class="icon">⏱️</i>
            {{ observation.duration }}
          </span>
          <span class="meta-badge type-badge" :class="observation.type">
            {{ observation.type === 'ciblée' ? '🎯 Ciblée' : '📝 Libre' }}
          </span>
        </div>
      </div>
      <button @click="$emit('close')" class="btn-close" aria-label="Fermer">✕</button>
    </div>

    <div class="detail-content">
      <!-- Section 1: Informations générales -->
      <section class="detail-section">
        <div class="section-header">
          <div class="section-icon">📋</div>
          <h3 class="section-title">Informations générales</h3>
        </div>
        <div class="section-body">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Source</span>
              <span class="info-value">{{ getSourceLabel(observation.observationSource) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Date</span>
              <span class="info-value">{{ formatDate(observation.date) }}</span>
            </div>
            <div class="info-item" v-if="observation.startTime">
              <span class="info-label">Heure de début</span>
              <span class="info-value">{{ observation.startTime }}</span>
            </div>
            <div class="info-item" v-if="observation.endTime">
              <span class="info-label">Heure de fin</span>
              <span class="info-value">{{ observation.endTime }}</span>
            </div>
            <div class="info-item" v-if="observation.duration">
              <span class="info-label">Durée</span>
              <span class="info-value highlight">{{ observation.duration }}</span>
            </div>
            <div class="info-item" v-if="observation.observerPosition">
              <span class="info-label">Position de l'observateur</span>
              <span class="info-value">{{ getObserverPositionLabel(observation.observerPosition) }}</span>
            </div>
          </div>

          <div v-if="observation.peoplePresent && observation.peoplePresent.length > 0" class="people-present">
            <span class="info-label">Personnes présentes</span>
            <div class="tags-list">
              <span v-for="person in observation.peoplePresent" :key="person" class="tag">
                {{ person }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: Contexte environnemental -->
      <section class="detail-section" v-if="hasEnvironmentData">
        <div class="section-header">
          <div class="section-icon">🌍</div>
          <h3 class="section-title">Contexte environnemental</h3>
        </div>
        <div class="section-body">
          <div class="info-grid">
            <div class="info-item" v-if="observation.location?.type">
              <span class="info-label">Type de lieu</span>
              <span class="info-value">{{ getLocationTypeLabel(observation.location.type) }}</span>
            </div>
            <div class="info-item" v-if="observation.environment?.socialSphere">
              <span class="info-label">Sphère</span>
              <span class="info-value">{{ getSocialSphereLabel(observation.environment.socialSphere) }}</span>
            </div>
          </div>
          <div class="info-item full-width" v-if="observation.location?.description">
            <span class="info-label">Description du lieu</span>
            <p class="info-text">{{ observation.location.description }}</p>
          </div>

          <div class="subsection" v-if="hasEnvironmentElements">
            <h4 class="subsection-title">Éléments sensoriels</h4>
            <div class="info-grid">
              <div class="info-item" v-if="observation.environment?.noiseLevel">
                <span class="info-label">Niveau sonore</span>
                <span class="info-value">{{ getNoiseLevelLabel(observation.environment.noiseLevel) }}</span>
              </div>
              <div class="info-item" v-if="observation.environment?.lighting">
                <span class="info-label">Luminosité</span>
                <span class="info-value">{{ getLightingLabel(observation.environment.lighting) }}</span>
              </div>
              <div class="info-item" v-if="observation.environment?.peopleCount">
                <span class="info-label">Densité sociale</span>
                <span class="info-value">{{ getCrowdingLabel(observation.environment.peopleCount) }}</span>
              </div>
            </div>
            <div class="info-item full-width" v-if="observation.environment?.otherSensory">
              <span class="info-label">Autres éléments sensoriels</span>
              <p class="info-text">{{ observation.environment.otherSensory }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 3: État avant la situation -->
      <section class="detail-section" v-if="hasBeforeState">
        <div class="section-header">
          <div class="section-icon">👤</div>
          <h3 class="section-title">État avant la situation</h3>
        </div>
        <div class="section-body">
          <div class="subsection" v-if="hasYoungBeforeData">
            <h4 class="subsection-title">Le jeune</h4>
            <div class="info-grid">
              <div class="info-item" v-if="observation.youngBefore?.emotionalState">
                <span class="info-label">État émotionnel</span>
                <span class="info-value badge-emotion" :class="observation.youngBefore.emotionalState">
                  {{ getEmotionalStateLabel(observation.youngBefore.emotionalState) }}
                </span>
              </div>
              <div class="info-item full-width" v-if="observation.youngBefore?.behavior">
                <span class="info-label">Comportement général</span>
                <p class="info-text">{{ observation.youngBefore.behavior }}</p>
              </div>
              <div class="info-item full-width" v-if="observation.youngBefore?.physiologicalNeeds && observation.youngBefore.physiologicalNeeds.length > 0">
                <span class="info-label">Besoins physiologiques (Satisfaits)</span>
                <div class="tags-list">
                  <span v-for="need in observation.youngBefore.physiologicalNeeds" :key="need" class="tag">
                    {{ getPhysiologicalNeedLabel(need) }}
                  </span>
                </div>
              </div>
              <div class="info-item full-width" v-if="observation.youngBefore?.preparation">
                <span class="info-label">Préparation à la situation</span>
                <p class="info-text">{{ observation.youngBefore.preparation }}</p>
              </div>
            </div>
          </div>

          <div class="subsection" v-if="hasObserverBeforeData">
            <h4 class="subsection-title">L'observateur</h4>
            <div class="info-grid">
              <div class="info-item full-width" v-if="observation.observerBefore?.emotionalStates && observation.observerBefore.emotionalStates.length > 0">
                <span class="info-label">État émotionnel</span>
                <div class="tags-list">
                  <span v-for="emotion in observation.observerBefore.emotionalStates" :key="emotion" class="tag">
                    {{ emotion }}
                  </span>
                </div>
              </div>
              <div class="info-item" v-if="observation.observerBefore?.availability">
                <span class="info-label">Disponibilité</span>
                <span class="info-value">{{ getAvailabilityLabel(observation.observerBefore.availability) }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 4: Description factuelle -->
      <section class="detail-section" v-if="observation.factualDescription">
        <div class="section-header">
          <div class="section-icon">📝</div>
          <h3 class="section-title">Description factuelle</h3>
        </div>
        <div class="section-body">
          <div class="factual-description">
            <p class="info-text">{{ observation.factualDescription }}</p>
          </div>
        </div>
      </section>

      <!-- Section 5: Interventions -->
      <section class="detail-section" v-if="hasInterventions">
        <div class="section-header">
          <div class="section-icon">🤝</div>
          <h3 class="section-title">Interventions de l'accompagnant</h3>
        </div>
        <div class="section-body">
          <div v-if="observation.interventions?.types && observation.interventions.types.length > 0" class="interventions-types">
            <span class="info-label">Types d'intervention</span>
            <div class="tags-list">
              <span v-for="type in observation.interventions.types" :key="type" class="tag intervention-tag">
                {{ getInterventionTypeLabel(type) }}
              </span>
            </div>
          </div>
          <div class="info-item full-width" v-if="observation.interventions?.description">
            <span class="info-label">Description des interventions</span>
            <p class="info-text">{{ observation.interventions.description }}</p>
          </div>
        </div>
      </section>

      <!-- Section 6: État après la situation -->
      <section class="detail-section" v-if="hasAfterState">
        <div class="section-header">
          <div class="section-icon">✨</div>
          <h3 class="section-title">État après la situation</h3>
        </div>
        <div class="section-body">
          <div class="subsection" v-if="hasYoungAfterData">
            <h4 class="subsection-title">Le jeune</h4>
            <div class="info-grid">
              <div class="info-item" v-if="observation.youngAfter?.emotionalState">
                <span class="info-label">État émotionnel</span>
                <span class="info-value badge-emotion" :class="observation.youngAfter.emotionalState">
                  {{ getEmotionalStateLabel(observation.youngAfter.emotionalState) }}
                </span>
              </div>
              <div class="info-item full-width" v-if="observation.youngAfter?.behavior">
                <span class="info-label">Comportement</span>
                <p class="info-text">{{ observation.youngAfter.behavior }}</p>
              </div>
              <div class="info-item full-width" v-if="observation.youngAfter?.decompensationSigns && observation.youngAfter.decompensationSigns.length > 0">
                <span class="info-label">Signes de surcharge / Décompensation</span>
                <div class="tags-list">
                  <span v-for="sign in observation.youngAfter.decompensationSigns" :key="sign" class="tag alert-tag">
                    {{ getDecompensationLabel(sign) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="subsection" v-if="observation.accompanistActions && observation.accompanistActions.length > 0">
            <h4 class="subsection-title">Actions de l'accompagnant</h4>
            <div class="tags-list">
              <span v-for="action in observation.accompanistActions" :key="action" class="tag action-tag">
                {{ getAccompanistActionLabel(action) }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 7: Compétences observées -->
      <section class="detail-section" v-if="observation.competences && observation.competences.length > 0">
        <div class="section-header">
          <div class="section-icon">⭐</div>
          <h3 class="section-title">Compétences observées</h3>
        </div>
        <div class="section-body">
          <div class="competences-list-detail">
            <div v-for="competence in observation.competences" :key="competence" class="competence-card-detail">
              <div class="comp-info">
                <span class="competence-icon">✓</span>
                <span class="competence-name">{{ getCompetenceName(competence) }}</span>
              </div>
              <div v-if="observation.competenceRatings && observation.competenceRatings[competence]" class="comp-rating">
                <span class="rating-stars">
                  <span v-for="n in 5" :key="n" class="star-small" :class="{ filled: n <= observation.competenceRatings[competence] }">★</span>
                </span>
                <span class="rating-text">{{ observation.competenceRatings[competence] }}/5</span>
              </div>
            </div>
          </div>
          <div class="info-item full-width" v-if="observation.competencesContext">
            <span class="info-label">Contexte de manifestation</span>
            <p class="info-text">{{ observation.competencesContext }}</p>
          </div>
        </div>
      </section>

      <!-- Section 8: Notes supplémentaires -->
      <section class="detail-section" v-if="observation.additionalNotes">
        <div class="section-header">
          <div class="section-icon">💭</div>
          <h3 class="section-title">Notes supplémentaires</h3>
        </div>
        <div class="section-body">
          <p class="info-text">{{ observation.additionalNotes }}</p>
        </div>
      </section>
    </div>

    <div class="detail-footer">
      <button @click="$emit('delete', observation)" class="btn-danger">
        <span class="btn-icon">🗑️</span>
        Supprimer
      </button>
      <div class="footer-right-actions">
        <button @click="$emit('close')" class="btn-secondary">Fermer</button>
        <button @click="$emit('edit', observation)" class="btn-primary">
          <span class="btn-icon">✏️</span>
          Modifier
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ObservationDetail',
  props: {
    observation: {
      type: Object,
      required: true
    }
  },
  computed: {
    hasEnvironmentData() {
      return this.observation.location || this.observation.environment;
    },
    hasEnvironmentElements() {
      const env = this.observation.environment;
      return env && (env.noiseLevel || env.lighting || env.peopleCount || env.otherSensory);
    },
    hasBeforeState() {
      return this.observation.youngBefore || this.observation.observerBefore;
    },
    hasYoungBeforeData() {
      const yb = this.observation.youngBefore;
      return yb && (yb.emotionalState || yb.behavior || (yb.physiologicalNeeds && yb.physiologicalNeeds.length > 0) || yb.preparation);
    },
    hasObserverBeforeData() {
      const ob = this.observation.observerBefore;
      return ob && ((ob.emotionalStates && ob.emotionalStates.length > 0) || ob.availability);
    },
    hasInterventions() {
      const int = this.observation.interventions;
      return int && (int.types?.length > 0 || int.description);
    },
    hasAfterState() {
      return this.observation.youngAfter || this.observation.accompanistActions?.length > 0;
    },
    hasYoungAfterData() {
      const ya = this.observation.youngAfter;
      return ya && (ya.emotionalState || ya.behavior || (ya.decompensationSigns && ya.decompensationSigns.length > 0));
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'Date non spécifiée';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getSourceLabel(value) {
      const labels = {
        'direct': 'Observation directe',
        'indirect': 'Rapporté par un tiers'
      };
      return labels[value] || value || 'Non spécifié';
    },
    getObserverPositionLabel(value) {
      const labels = {
        'active': 'Actif (participant à la situation)',
        'passive': 'Passif (observateur externe)'
      };
      return labels[value] || value;
    },
    getLocationTypeLabel(value) {
      const labels = {
        'interior': 'Intérieur',
        'exterior': 'Extérieur'
      };
      return labels[value] || value;
    },
    getSocialSphereLabel(value) {
      const labels = {
        'privee': 'Privée (Maison, famille)',
        'publique': 'Publique (École, parc, magasin)'
      };
      return labels[value] || value;
    },
    getNoiseLevelLabel(value) {
      const labels = {
        'calme': 'Calme',
        'modere': 'Modéré',
        'bruyant': 'Bruyant'
      };
      return labels[value] || value;
    },
    getLightingLabel(value) {
      const labels = {
        'tamisee': 'Tamisée',
        'normale': 'Normale',
        'forte': 'Forte'
      };
      return labels[value] || value;
    },
    getCrowdingLabel(value) {
      const labels = {
        'seul': 'Seul',
        'petit': 'Petit groupe (calme)',
        'dense': 'Groupe dense',
        'foule': 'Foule (sensation de monde)'
      };
      return labels[value] || value;
    },
    getEmotionalStateLabel(value) {
      const labels = {
        'positif': 'Positif',
        'negatif': 'Négatif',
        'neutre': 'Neutre / Je ne sais pas',
        'calme': 'Calme 😌',
        'joyeux': 'Joyeux 😊',
        'fier': 'Fier 😎',
        'fatigue': 'Fatigué 😴',
        'frustre': 'Frustré 😤',
        'soulage': 'Soulagé 😅',
        'unknown': 'Je ne sais pas ❓'
      };
      return labels[value] || value;
    },
    getPhysiologicalNeedLabel(value) {
      const labels = {
        'a_mange': 'A mangé',
        'a_dormi': 'A bien dormi',
        'est_repose': 'Est reposé',
        'a_ete_toilettes': 'A été aux toilettes'
      };
      return labels[value] || value;
    },
    getAvailabilityLabel(value) {
      const labels = {
        'totale': 'Totalement disponible',
        'partielle': 'Partiellement disponible',
        'limitee': 'Disponibilité limitée'
      };
      return labels[value] || value;
    },
    getInterventionTypeLabel(value) {
      const labels = {
        'aucune': 'Aucune intervention',
        'guidage_verbal': 'Intervention verbale',
        'guidage_physique': 'Intervention physique',
        'encouragement': 'Encouragement'
      };
      return labels[value] || value;
    },
    getAccompanistActionLabel(value) {
      const labels = {
        'felicitations': 'Félicitations',
        'encouragements': 'Encouragements',
        'debriefing': 'Debriefing',
        'autre': 'Autre'
      };
      return labels[value] || value;
    },
    getDecompensationLabel(value) {
      const labels = {
        'flapping': 'Flapping / Stéréotypie',
        'auto_mutilation': 'Auto-mutilation',
        'repli': 'Repli sur soi',
        'refuge': 'Besoin de refuge',
        'autre': 'Autre'
      };
      return labels[value] || value;
    },
    getCompetenceName(key) {
      // This is a simplified mapping. Ideally, this should come from a shared constant or service.
      // For display purposes in detail view, mapping keys to readable names.
      const names = {
        'com_claire': 'Communication claire et précise',
        'ecoute': 'Écoute et compréhension',
        'collaboration': 'Collaboration en équipe',
        'gestion_interactions': 'Gestion des interactions',
        'planification': 'Planification et anticipation',
        'rigueur': 'Rigueur et précision',
        'execution': 'Exécution et concentration',
        'verification': 'Vérification et contrôle',
        'autonomie': 'Autonomie dans le travail',
        'demande_aide': 'Demande d\'aide appropriée',
        'initiative': 'Prise d\'initiative',
        'decision': 'Prise de décision',
        'gestion_changement': 'Gestion du changement',
        'flexibilite': 'Flexibilité situationnelle',
        'perseverance': 'Persévérance et engagement',
        'motivation': 'Motivation et implication',
        'gestion_stress': 'Gestion du stress',
        'gestion_emotionnelle': 'Gestion émotionnelle',
        'prise_recul': 'Prise de recul',
        'reactivite': 'Réactivité maîtrisée',
        'curiosite': 'Curiosité professionnelle',
        'apprentissage': 'Apprentissage continu',
        'ouverture': 'Ouverture à la nouveauté',
        // Keep old keys for backward compatibility if needed or example data
        'patience': 'Patience',
        'empathie': 'Empathie',
        'adaptabilite': 'Adaptabilité',
        'creativite': 'Créativité',
        'organisation': 'Organisation',
        'confiance_en_soi': 'Confiance en soi'
      };
      return names[key] || key;
    }
  }
};
</script>

<style scoped>
.observation-detail-container {
  background: linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%);
  border-radius: 20px;
  max-width: 1100px;
  margin: 0 auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: visible;
  animation: slideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2.5rem;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-bottom: none;
  position: relative;
  overflow: hidden;
}

.detail-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  pointer-events: none;
}

.header-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.detail-title {
  font-size: 1.875rem;
  font-weight: 800;
  color: white;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.observation-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.meta-badge .icon {
  font-size: 1rem;
}

.type-badge {
  font-weight: 700;
}

.type-badge.ciblée {
  background: rgba(34, 197, 94, 0.3);
  border-color: rgba(34, 197, 94, 0.5);
}

.type-badge.libre {
  background: rgba(234, 88, 12, 0.3);
  border-color: rgba(234, 88, 12, 0.5);
}

.btn-close {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  font-size: 1.5rem;
  color: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: rotate(90deg) scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Content */
.detail-content {
  padding: 2.5rem;
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.detail-content::-webkit-scrollbar {
  width: 8px;
}

.detail-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.detail-content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 4px;
}

.detail-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
}

/* Sections */
.detail-section {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid transparent;
  background-image: linear-gradient(white, white),
                    linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.detail-section:hover {
  box-shadow: 0 8px 24px rgba(255, 152, 0, 0.15);
  transform: translateY(-2px);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f1f5f9;
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  width: 48px;
  height: 48px;
  border-radius: 12px;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.section-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Info items */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  padding: 0.625rem 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #FF9800;
}

.info-value.highlight {
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  border-left-color: #FF9800;
  color: #B85C00;
  font-weight: 700;
}

.info-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #475569;
  margin: 0;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #FF9800;
}

/* Tags */
.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  margin-top: 0.5rem;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  color: #B85C00;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #FB923C;
  transition: all 0.2s ease;
}

.tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.2);
}

.intervention-tag {
  background: linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%);
  color: #B85C00;
  border-color: #FB923C;
}

.action-tag {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #15803d;
  border-color: #86efac;
}

/* Badges */
.badge-emotion {
  display: inline-block;
  padding: 0.625rem 1rem;
  font-weight: 700;
  text-align: center;
}

.badge-emotion.calme {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.badge-emotion.joyeux {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.badge-emotion.anxieux {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
}

.badge-emotion.frustre {
  background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%);
  color: #9a3412;
}

.badge-emotion.fatigue {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #3730a3;
}

.badge-emotion.excite {
  background: linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%);
  color: #9f1239;
}

.badge-emotion.neutre {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
}

/* Subsections */
.subsection {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.subsection-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #334155;
  margin: 0 0 1rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

/* People present */
.people-present {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

/* Interventions types */
.interventions-types {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Factual description */
.factual-description {
  padding: 1.5rem;
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border-radius: 12px;
  border-left: 4px solid #eab308;
}

/* Compétences */
.competences-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.competence-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: white;
  border: 2px solid #dcfce7;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.competence-card:hover {
  border-color: #86efac;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);
  transform: translateX(4px);
}

.competence-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
}

.competence-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #15803d;
  line-height: 1.4;
}

.competences-list-detail {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.competence-card-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: white;
  border: 2px solid #dcfce7;
  border-radius: 12px;
}

.comp-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.comp-rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.star-small {
  color: #D1D5DB;
  font-size: 1rem;
}

.star-small.filled {
  color: #F59E0B;
}

.rating-text {
  font-weight: 700;
  color: #92400E;
  font-size: 0.875rem;
}

.alert-tag {
  background: linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%);
  color: #B91C1C;
  border-color: #FCA5A5;
}

/* Footer */
.detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 2.5rem;
  background: linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%);
  border-top: 2px solid #e2e8f0;
}

.footer-right-actions {
  display: flex;
  gap: 1rem;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.3);
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.btn-primary:hover::before {
  left: 100%;
}

.btn-primary:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-2px);
}

.btn-danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);
}

.btn-danger::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.btn-danger:hover::before {
  left: 100%;
}

.btn-danger:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
}

.btn-icon {
  font-size: 1.125rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .observation-detail-container {
    border-radius: 0;
    box-shadow: none;
  }

  .detail-header {
    padding: 1.5rem;
    flex-direction: column;
    gap: 1rem;
    flex-shrink: 0;
  }

  .detail-title {
    font-size: 1.5rem;
  }

  .observation-meta {
    flex-direction: column;
  }

  .btn-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .detail-content {
    padding: 1.5rem;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .detail-section {
    padding: 1.5rem;
  }

  .section-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .competences-list {
    grid-template-columns: 1fr;
  }

  .detail-footer {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.5rem;
    flex-shrink: 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  }

  .footer-right-actions {
    flex-direction: column-reverse;
    width: 100%;
  }

  .btn-primary,
  .btn-secondary,
  .btn-danger {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .detail-header {
    padding: 1.25rem;
  }

  .detail-title {
    font-size: 1.25rem;
  }

  .detail-content {
    padding: 1.25rem;
  }

  .detail-section {
    padding: 1.25rem;
  }
}

/* Accessibility */
*:focus-visible {
  outline: 3px solid #FF9800;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
